import axios from 'axios';
import { message } from 'utils/messageUtils';
import { errorNotification } from 'utils/notifications';
import { getAccessToken } from '@timeedit/ui-components';
import type {
  TAPIRequest,
  TAPIRequestShorthand,
  TOption,
} from '../types/api.type';
import { configService } from './config.service';

const prepareOption = <Data = any,>(
  requiresAuth: boolean,
  { data, headers, method, params, ...options }: TOption<Data> = {}
): TOption => {
  const option: TOption = {
    ...options,
    method,
    headers: {
      ...headers,
    },
  };

  if (requiresAuth) {
    const token = getAccessToken();

    option.headers = {
      Authorization: `Bearer ${token}`,
      ...headers,
    };
  }

  if (data) {
    if (method === 'GET') {
      option.params = { ...data, ...(params || {}) };
    } else {
      if (Array.isArray(data)) {
        option.data = [...data];
      } else {
        option.data = { ...data };
      }
      if (params) option.params = { ...params };
    }
  }
  return option;
};

/**
 * @function getAPIUrl
 * @description returns URL for TE Preferences API depending on environment
 * @param {String} endpoint route to be called
 * @returns {String}
 */
const getAPIUrl = (endpoint: string) => {
  if (endpoint.search('http://') > -1 || endpoint.search('https://') > -1)
    return endpoint;
  return `${configService.REACT_APP_BASE_URL}${endpoint}`;
};

const apiRequest = async <Data = any, ResponseType = any>({
  method,
  endpoint,
  absoluteUrl = false,
  data,
  params = null,
  requiresAuth = true,
  headers,
  successMessage = undefined,
  errorMessage = undefined,
  suppressNotification = false,
  responseType,
}: TAPIRequest<Data>): Promise<ResponseType> => {
  const fullUrl = !absoluteUrl ? getAPIUrl(endpoint) : endpoint;
  const option = prepareOption(requiresAuth, {
    method,
    data,
    headers,
    params,
    responseType,
  });
  try {
    const response = await axios<ResponseType>(fullUrl, option);
    /**
     * When to show success messages:
     * x) For all POST, PATCH, PUT, DELETE calls unless explicitely opted out of (i.e., successMessage = false)
     * x) For GET if explicitely asked for (i.e., successMessage !== undefined && !== false)
     */
    if (method === 'GET' && !!successMessage) {
      if (!suppressNotification) {
        message.success(successMessage);
      }
    }
    if (
      method !== 'GET' &&
      (successMessage === undefined || !!successMessage) &&
      !suppressNotification
    ) {
      message.success(
        typeof successMessage === 'string'
          ? successMessage
          : 'The operation was successful'
      );
    }
    return response.data;
  } catch (error: any) {
    /**
     * When to show success messages:
     * x) For all failed calls unless explicitely opted out of (i.e., errorMessage = false)
     * x) If errorMessage is specified, show the custom message - otherwise show error.message.toString()
     */

    if (
      !(error?.response?.data?.status === 401 && method === 'GET') &&
      !suppressNotification
    ) {
      const message =
        typeof errorMessage === 'string'
          ? errorMessage
          : error?.response?.data?.message;
      errorNotification(
        message || 'An error occurred while processing the request.'
      );
    }
    throw error;
  }
};

const getAndDeleteDefaults = <Data = any,>(
  method: 'GET' | 'DELETE',
  props: TAPIRequestShorthand<Data>
): TAPIRequest<Data> => ({
  method,
  endpoint: props.endpoint,
  absoluteUrl: props.absoluteUrl || false,
  data: props.data || undefined,
  requiresAuth: props.requiresAuth || true,
  headers: props.headers || undefined,
  successMessage: props.successMessage,
  errorMessage: props.errorMessage || undefined,
  suppressNotification: props.suppressNotification || false,
});

const postPutPatchDefaults = <Data = any,>(
  method: 'PUT' | 'PATCH' | 'POST',
  props: TAPIRequestShorthand<Data>
): TAPIRequest<Data> => ({
  method,
  endpoint: props.endpoint,
  absoluteUrl: props.absoluteUrl || false,
  data: props.data || undefined,
  params: props.params || undefined,
  requiresAuth: props.requiresAuth || true,
  headers: props.headers || undefined,
  successMessage: props.successMessage,
  successToastTitle: props.successToastTitle || undefined,
  errorMessage: props.errorMessage || undefined,
  suppressNotification: props.suppressNotification || false,
  responseType: props.responseType || undefined,
});

const apiService = {
  get: <Data = any, ResponseType = any>(props: TAPIRequestShorthand<Data>) =>
    apiRequest<Data, ResponseType>(getAndDeleteDefaults<Data>('GET', props)),
  post: <Data = any, ResponseType = any>(props: TAPIRequestShorthand<Data>) =>
    apiRequest<Data, ResponseType>(postPutPatchDefaults<Data>('POST', props)),
  put: <Data = any, ResponseType = any>(props: TAPIRequestShorthand<Data>) =>
    apiRequest<Data, ResponseType>(postPutPatchDefaults<Data>('PUT', props)),
  patch: <Data = any, ResponseType = any>(props: TAPIRequestShorthand<Data>) =>
    apiRequest<Data, ResponseType>(postPutPatchDefaults<Data>('PATCH', props)),
  delete: <Data = any, ResponseType = any>(props: TAPIRequestShorthand<Data>) =>
    apiRequest<Data, ResponseType>(getAndDeleteDefaults<Data>('DELETE', props)),
};

export default apiService;
